/* Cómo se lee «la última ubicación registrada».
 *
 * La regla de DÓNDE vive en la base (`ubicaciones_ultimas`). Esto es la otra
 * mitad: cómo se dice en una celda de tabla sin que se lea como algo que no es.
 *
 * ── LA TRAMPA QUE ESTO EVITA ──────────────────────────────────────────────
 *
 * «RGD» solo, en una columna, se lee como «está en Río Grande». Y puede ser un
 * dato de hace tres semanas. Por eso NUNCA sale el lugar sin la fecha al lado,
 * y por eso el detalle dice de qué fuente salió: «portería lo vio entrar
 * anoche» y «llegó a Río Grande el 24 de julio» son afirmaciones de peso muy
 * distinto y en una tabla se ven idénticas.
 *
 * Es puro: entra la fila, sale qué mostrar. Se prueba en una tabla.
 */

import { mediodiaHoyAr } from "./fechaAr";

/* Los códigos de las sedes, a como se llaman.
 *
 * ESTO NO ES FUSIONAR DATOS. En `movimientos.destino` conviven `RGL` (17) y
 * `RIO GALLEGOS` (134) para el mismo lugar, igual que pasa con los clientes.
 * Con los clientes la regla es no unirlos, porque ahí el nombre ES el dato y
 * unir dos formas arruina las estadísticas de las dos sin dejar rastro. Acá no
 * se cuenta nada: es una etiqueta en una celda, y mostrar las dos formas haría
 * que la misma sede se lea como dos lugares distintos.
 *
 * Lo que NO está en la tabla se muestra tal cual se escribió. Traducir a ojo lo
 * que no se conoce —`SAO`, `INO`, `GRACA`— sería inventar un lugar. */
const SEDES = {
  RGD: "Río Grande",
  RGL: "Río Gallegos",
  "RIO GALLEGOS": "Río Gallegos",
  USH: "Ushuaia",
  BUE: "Buenos Aires",
  "BUENOS AIRES": "Buenos Aires",
};

export function nombreDeLugar(lugar) {
  const l = (lugar || "").trim();
  if (!l) return "";
  return SEDES[l.toUpperCase()] || l;
}

/* «hoy» y «ayer» y no la fecha: son los dos casos en los que el dato vale, y
 * leer «07/08/26» obliga a hacer la cuenta para darse cuenta de que es de
 * anteayer. Del tercer día en adelante la fecha exacta dice más. */
export function cuando(fecha, hoy = mediodiaHoyAr()) {
  if (!fecha) return "";
  const f = new Date(String(fecha).slice(0, 10) + "T12:00:00");
  if (Number.isNaN(f.getTime())) return "";
  const dias = Math.round((mediodia(hoy) - f) / 86400000);
  if (dias === 0) return "hoy";
  if (dias === 1) return "ayer";
  return f.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

const mediodia = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);

const diasDesde = (fecha, hoy) => {
  const f = new Date(String(fecha).slice(0, 10) + "T12:00:00");
  if (Number.isNaN(f.getTime())) return null;
  return Math.round((mediodia(hoy) - f) / 86400000);
};

const FUENTE = {
  porteria: "lo registró portería en el portón",
  movimiento: "sale del movimiento de unidades",
};

/* Devuelve siempre las mismas claves, tenga o no dato:
 *
 *   texto   · lo que se ve en la celda
 *   fecha   · lo que se ve al lado, ya escrito
 *   detalle · el `title`, que explica de dónde salió y hace cuánto
 *   vacio   · true cuando no hay ningún registro
 *
 * `vacio` es una clave y no la ausencia de las otras: la celda sin dato dibuja
 * «—», y un `undefined` que la pantalla tenga que interpretar termina en un
 * `if` distinto en cada tabla.
 */
export function ubicacion(fila, hoy = mediodiaHoyAr()) {
  const estado = fila?.ubic_estado || fila?.estado || null;
  const fecha = fila?.ubic_fecha || fila?.fecha || null;
  const fuente = fila?.ubic_fuente || fila?.fuente || null;
  const lugar = nombreDeLugar(fila?.ubic_lugar ?? fila?.lugar);

  if (!estado || !fecha) {
    /* Y el detalle lo DICE, en vez de dejar la celda muda. Un «—» sin
     * explicación se lee como un error de la pantalla; con la frase se lee
     * como lo que es —de esta unidad no hay nada anotado— y eso es
     * accionable: se carga. */
    return {
      texto: "—",
      fecha: "",
      detalle:
        "No hay ningún registro de esta unidad: ni un movimiento de portería ni un tramo cargado.",
      vacio: true,
    };
  }

  const texto =
    estado === "base"
      ? lugar || "En base"
      : estado === "salio"
        ? "Salió de base"
        : estado === "en_viaje"
          ? lugar
            ? `En viaje a ${lugar}`
            : "En viaje"
          : lugar || "Sin destino anotado";

  const d = diasDesde(fecha, hoy);
  const hace =
    d === null || d < 0 ? "" : d === 0 ? "hoy" : d === 1 ? "hace 1 día" : `hace ${d} días`;

  /* «Salió» y «en viaje» dicen cosas distintas y el detalle lo explica, porque
   * en la celda se parecen. Un tramo abierto de hace tres semanas casi nunca es
   * un camión de viaje: es una llegada que nadie cargó. */
  const que =
    estado === "base"
      ? "Entró a la base y no se registró que saliera"
      : estado === "salio"
        ? "Salió de la base. A dónde fue no está cargado"
        : estado === "en_viaje"
          ? "Tiene un tramo salido y sin llegada cargada"
          : "Llegada del último tramo cerrado";

  return {
    texto,
    fecha: cuando(fecha, hoy),
    detalle: [que, FUENTE[fuente], hace].filter(Boolean).join(" · "),
    vacio: false,
  };
}
